import React, { useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon,
    Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3,
    Quote, Undo, Redo, Palette, Strikethrough
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_SWATCHES = ['#1A1A2E', '#5B2D8E', '#D8B4E2', '#B08D3F', '#4A4A68', '#8B0000', '#0B6E4F', '#F9F6F0'];

/**
 * Reusable WYSIWYG editor built on TipTap.
 * - value: HTML string
 * - onChange: (html) => void
 * - placeholder: helper text shown above the editor
 * - minHeight: css min-height for the editor area (default 200px)
 * - variant: 'full' (default) | 'compact' (fewer buttons, no headings/images)
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = '200px', variant = 'full' }) {
    const fileInputRef = useRef(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Underline,
            TextStyle,
            Color,
            Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'underline text-[#5B2D8E]', rel: 'noopener noreferrer', target: '_blank' } }),
            Image.configure({ HTMLAttributes: { class: 'rounded-sm my-4 max-w-full h-auto' } }),
            TextAlign.configure({ types: ['heading', 'paragraph'] })
        ],
        content: value || '',
        editorProps: {
            attributes: {
                class: 'prose max-w-none focus:outline-none px-4 py-3 text-[#1A1A2E]',
                'data-testid': 'rte-content'
            }
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange && onChange(html === '<p></p>' ? '' : html);
        }
    });

    // Keep editor content in sync when parent value changes (e.g., opening the edit dialog)
    React.useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const incoming = value || '';
        if (incoming !== current && !(incoming === '' && current === '<p></p>')) {
            editor.commands.setContent(incoming, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    const setLink = useCallback(() => {
        if (!editor) return;
        const prev = editor.getAttributes('link').href;
        const url = window.prompt('URL', prev || 'https://');
        if (url === null) return;
        if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    const uploadImage = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        try {
            const { data } = await axios.post(`${API_URL}/api/upload/image`, fd, { withCredentials: true });
            const absolute = `${API_URL}${data.url}`;
            editor.chain().focus().setImage({ src: absolute, alt: file.name || 'image' }).run();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Image upload failed');
        }
    };

    if (!editor) return null;

    const btn = 'p-2 rounded hover:bg-[#F2EBE5] text-[#4A4A68] disabled:opacity-30 transition';
    const active = 'bg-[#5B2D8E] text-white hover:bg-[#5B2D8E]';

    return (
        <div className="border border-[#E5E0D8] rounded-sm bg-white overflow-hidden" data-testid="rich-text-editor">
            <div className="flex flex-wrap items-center gap-1 border-b border-[#E5E0D8] px-2 py-1 bg-[#F9F6F0]">
                <button type="button" title="Undo" onClick={() => editor.chain().focus().undo().run()} className={btn} data-testid="rte-undo"><Undo className="w-4 h-4" /></button>
                <button type="button" title="Redo" onClick={() => editor.chain().focus().redo().run()} className={btn} data-testid="rte-redo"><Redo className="w-4 h-4" /></button>
                <span className="w-px h-6 bg-[#E5E0D8] mx-1" />
                {variant === 'full' && (<>
                    <button type="button" title="Heading 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`${btn} ${editor.isActive('heading', { level: 1 }) ? active : ''}`} data-testid="rte-h1"><Heading1 className="w-4 h-4" /></button>
                    <button type="button" title="Heading 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`${btn} ${editor.isActive('heading', { level: 2 }) ? active : ''}`} data-testid="rte-h2"><Heading2 className="w-4 h-4" /></button>
                    <button type="button" title="Heading 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`${btn} ${editor.isActive('heading', { level: 3 }) ? active : ''}`} data-testid="rte-h3"><Heading3 className="w-4 h-4" /></button>
                    <span className="w-px h-6 bg-[#E5E0D8] mx-1" />
                </>)}
                <button type="button" title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} className={`${btn} ${editor.isActive('bold') ? active : ''}`} data-testid="rte-bold"><Bold className="w-4 h-4" /></button>
                <button type="button" title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} className={`${btn} ${editor.isActive('italic') ? active : ''}`} data-testid="rte-italic"><Italic className="w-4 h-4" /></button>
                <button type="button" title="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()} className={`${btn} ${editor.isActive('underline') ? active : ''}`} data-testid="rte-underline"><UnderlineIcon className="w-4 h-4" /></button>
                <button type="button" title="Strikethrough" onClick={() => editor.chain().focus().toggleStrike().run()} className={`${btn} ${editor.isActive('strike') ? active : ''}`} data-testid="rte-strike"><Strikethrough className="w-4 h-4" /></button>
                <span className="w-px h-6 bg-[#E5E0D8] mx-1" />
                <button type="button" title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${btn} ${editor.isActive('bulletList') ? active : ''}`} data-testid="rte-ul"><List className="w-4 h-4" /></button>
                <button type="button" title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${btn} ${editor.isActive('orderedList') ? active : ''}`} data-testid="rte-ol"><ListOrdered className="w-4 h-4" /></button>
                <button type="button" title="Blockquote" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`${btn} ${editor.isActive('blockquote') ? active : ''}`} data-testid="rte-quote"><Quote className="w-4 h-4" /></button>
                <span className="w-px h-6 bg-[#E5E0D8] mx-1" />
                <button type="button" title="Align left" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={`${btn} ${editor.isActive({ textAlign: 'left' }) ? active : ''}`} data-testid="rte-align-left"><AlignLeft className="w-4 h-4" /></button>
                <button type="button" title="Align center" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={`${btn} ${editor.isActive({ textAlign: 'center' }) ? active : ''}`} data-testid="rte-align-center"><AlignCenter className="w-4 h-4" /></button>
                <button type="button" title="Align right" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={`${btn} ${editor.isActive({ textAlign: 'right' }) ? active : ''}`} data-testid="rte-align-right"><AlignRight className="w-4 h-4" /></button>
                <span className="w-px h-6 bg-[#E5E0D8] mx-1" />
                <button type="button" title="Link" onClick={setLink} className={`${btn} ${editor.isActive('link') ? active : ''}`} data-testid="rte-link"><LinkIcon className="w-4 h-4" /></button>
                {variant === 'full' && (
                    <>
                        <button type="button" title="Insert image" onClick={() => fileInputRef.current?.click()} className={btn} data-testid="rte-image"><ImageIcon className="w-4 h-4" /></button>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} className="hidden" />
                    </>
                )}
                <div className="relative group">
                    <button type="button" title="Text color" className={btn} data-testid="rte-color"><Palette className="w-4 h-4" /></button>
                    <div className="hidden group-hover:flex absolute z-10 top-full left-0 bg-white border border-[#E5E0D8] rounded-sm shadow-md p-2 gap-1">
                        {COLOR_SWATCHES.map(c => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => editor.chain().focus().setColor(c).run()}
                                className="w-5 h-5 rounded-sm border border-[#E5E0D8]"
                                style={{ background: c }}
                                data-testid={`rte-color-${c.slice(1)}`}
                            />
                        ))}
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().unsetColor().run()}
                            className="w-5 h-5 rounded-sm border border-[#E5E0D8] bg-white text-[10px]"
                            title="Reset color"
                            data-testid="rte-color-reset"
                        >×</button>
                    </div>
                </div>
            </div>
            {placeholder && !value && (
                <p className="px-4 pt-3 text-sm text-[#8A8A9E] italic">{placeholder}</p>
            )}
            <div style={{ minHeight }} className="text-[#1A1A2E]">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}
